import { useCallback, useEffect, useState } from "react";

import type {
  CreateEmergencyLightCheckResponseDto,
  EmergencyLightCheckItemDto,
} from "@/generated/model";
import type { EmergencyLightCheckKind } from "@/lib/emergency-light-checks";
import {
  cachedUserId,
  generateClientId,
  offlineDb,
  requireCachedUserId,
} from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

/**
 * `queued` means offline or no answer from the server. `failed` means the server
 * rejected it: the sync queue swallows HTTP failures, so awaiting alone can't tell (NAU-391).
 */
export type EmergencyLightCheckOutcome = "synced" | "queued" | "failed";

export interface EmergencyLightCheckSubmission {
  nurseryId: string;
  kind: EmergencyLightCheckKind;
  items: EmergencyLightCheckItemDto[];
}

export interface EmergencyLightCheckResult {
  outcome: EmergencyLightCheckOutcome;
  /** Only set on `synced` — a queued check has no server ids yet. */
  created?: CreateEmergencyLightCheckResponseDto;
  /** Only set on `failed` — the server's reason, e.g. which light was missed. */
  error?: string;
}

interface UseOfflineEmergencyLightCheckResult {
  submit: (
    submission: EmergencyLightCheckSubmission,
  ) => Promise<EmergencyLightCheckResult>;
  isPending: boolean;
  syncStatus: SyncStatus;
}

/**
 * Submit an emergency lighting check online or offline. The whole check is queued
 * as ONE item: the server's AC13 completeness rule is all-or-nothing.
 */
export function useOfflineEmergencyLightCheck(): UseOfflineEmergencyLightCheckResult {
  const [isPending, setIsPending] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    pendingCount: 0,
    errorCount: 0,
    isSyncing: false,
    isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  });

  useEffect(() => {
    syncService.getStatus().then(setSyncStatus);
    const unsubscribe = syncService.subscribe(setSyncStatus);
    // The sync service only notifies on sync activity, so losing signal after
    // the form opened would otherwise never show the offline banner.
    const setOnline = () =>
      setSyncStatus((prev) => ({ ...prev, isOnline: navigator.onLine }));
    window.addEventListener("online", setOnline);
    window.addEventListener("offline", setOnline);
    return () => {
      unsubscribe();
      window.removeEventListener("online", setOnline);
      window.removeEventListener("offline", setOnline);
    };
  }, []);

  const submit = useCallback(
    async (submission: EmergencyLightCheckSubmission) => {
      setIsPending(true);
      try {
        const localId = generateClientId();

        await offlineDb.emergencyLightChecks.put({
          id: localId,
          nurseryId: submission.nurseryId,
          kind: submission.kind,
          items: submission.items,
          queuedAt: new Date().toISOString(),
          // requireCachedUserId, not cachedUserId: the sync layer omits a falsy
          // author, so the server would credit whoever drains the queue.
          capturedByUserId: requireCachedUserId(await cachedUserId()),
          syncState: "pending",
        });

        await offlineDb.syncQueue.add({
          id: generateClientId(),
          type: "emergencyLightCheck",
          entityId: localId,
          // Same band as the other monitoring submissions: ahead of photo
          // uploads, behind anything that unblocks another queued item.
          priority: 4,
          createdAt: Date.now(),
          attempts: 0,
        });

        if (!navigator.onLine) {
          return { outcome: "queued" as const };
        }

        await syncService.immediateSync();

        // The queue swallows a failed replay, so immediateSync resolving proves
        // nothing; the stored record is the only signal the server took it.
        const stored = await offlineDb.emergencyLightChecks.get(localId);
        if (stored?.syncState === "synced") {
          return {
            outcome: "synced" as const,
            created:
              stored.serverId && stored.serverItems
                ? { id: stored.serverId, items: stored.serverItems }
                : undefined,
          };
        }

        // No server reason means no answer (e.g. weak signal); the queue retries it.
        if (stored && !stored.lastError) {
          return { outcome: "queued" as const };
        }

        // Rejected: drop the queued copy, or the queue's retry plus the user's
        // corrected resubmission would record the inspection twice.
        await offlineDb.syncQueue
          .filter(
            (i) => i.type === "emergencyLightCheck" && i.entityId === localId,
          )
          .delete();
        await offlineDb.emergencyLightChecks.delete(localId);
        return { outcome: "failed" as const, error: stored?.lastError };
      } finally {
        setIsPending(false);
      }
    },
    [],
  );

  return { submit, isPending, syncStatus };
}
